// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import moment from 'moment'
import React from 'react'
import { get } from 'react-hook-form'
import Loader from '../../common/loader/Loader'
import { Query } from '../../react-apollo/Components'
import { RestrictedConsumer } from '../auth/RestrictedContext'
import { i18n } from '../index'
import RequiresAction from './RequiresAction'
import { fetchMonthlyInfo } from './monthlyInfoQueries'
import { MonthlyInfoGeneral, MonthlyInfoVariables } from './monthlyInfoTypes'
import css from './salesAndHours.css'

type MonthlyInfoGeneralTotal = {
  totalActualSales: number
  totalBudgetedSales: number
  totalPlannedHours: number
  totalPlannedSales: number
  totalActualHours: number
  actualEfficiency: number
  plannedEfficiency: number
  budgetedEfficiency: number
}

const calculateTotalData = (data: Array<MonthlyInfoGeneral>): MonthlyInfoGeneralTotal => {
  let totalActualSales = 0
  let totalPlannedSales = 0
  let totalPlannedHours = 0
  let totalBudgetedSales = 0
  let totalBudgetedHours = 0
  let totalActualHours = 0
  data.forEach((item) => {
    totalActualSales += item.totalActualSales
    totalPlannedSales += item.totalPlannedSales
    totalActualHours += item.totalActualHours
    totalPlannedHours += item.totalPlannedHours
    totalBudgetedHours += item.totalBudgetedHours
    totalBudgetedSales += item.totalBudgetedSales
  })
  return {
    totalActualSales: Math.round(totalActualSales),
    totalPlannedSales: Math.round(totalPlannedSales),
    totalActualHours: Math.round(totalActualHours),
    totalBudgetedSales: Math.round(totalBudgetedSales),
    totalPlannedHours: Math.round(totalPlannedHours),
    actualEfficiency: calculateEfficiency(totalActualSales, totalActualHours),
    budgetedEfficiency: calculateEfficiency(totalBudgetedSales, totalBudgetedHours),
    plannedEfficiency: calculateEfficiency(totalPlannedSales, totalPlannedHours),
  }
}

const calculateEfficiency = (actualSales: number, actualHours: number): number =>
  actualSales && actualHours ? Math.round(actualSales / actualHours) : 0

type SalesAndHoursProps = {
  stationId: string
  date: string
  type?: 'Hesburger' | 'Market' | 'Restaurant'
  showOnly?: 'Hesburger' | 'Market'
}

export default function SalesAndHours({ stationId, date, type, showOnly }: SalesAndHoursProps) {
  return (
    <Query<Array<MonthlyInfoGeneral>, MonthlyInfoVariables>
      query={fetchMonthlyInfo}
      variables={{
        stationId,
        date,
        type: type ?? 'General',
      }}
    >
      {({ data, error, loading, refetch }) => {
        const rows = get(data, 'monthlyInfo', []).filter((item) =>
          showOnly ? item.profitCenterType === showOnly : true,
        )

        const taxfree = showOnly === 'Market' // Show taxfree values on market page
        const budgetedSalesHeading = taxfree ? 'budgetedSalesTaxfree' : 'budgetedSales'
        const actualSalesHeading = taxfree ? 'actualSalesTaxfree' : 'actualSales'
        const plannedSalesHeading = taxfree ? 'plannedSalesInclTax' : 'plannedSales'
        const actualEfficiencyHeading = taxfree ? 'actualEfficiencyTaxfree' : 'actualEfficiency'
        const plannedEfficiencyHeading = taxfree ? 'plannedEfficiencyInclTax' : 'plannedEfficiency'
        const budgetedEfficiencyHeading = taxfree ? 'budgetedEfficiencyTaxfree' : 'budgetedEfficiency'

        return (
          <Loader
            {...{
              error,
              loading,
            }}
          >
            <div className={css.salesAndHours}>
              <Typography variant='h6' gutterBottom>
                {i18n.t('monthlyInfo.title.salesAndHours', {
                  month: moment(date).format('MMMM'),
                })}
              </Typography>
              <RestrictedConsumer>
                {(hasPermission) => (
                  <Paper>
                    <Table className={css.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell align='center' className={css.tableHeading} />
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t(`monthlyInfo.heading.${budgetedSalesHeading}`)}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t(`monthlyInfo.heading.${actualSalesHeading}`)}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t(`monthlyInfo.heading.${plannedSalesHeading}`)}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('monthlyInfo.heading.actualHours')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t('monthlyInfo.heading.plannedHours')}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t(`monthlyInfo.heading.${actualEfficiencyHeading}`)}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t(`monthlyInfo.heading.${plannedEfficiencyHeading}`)}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                            {i18n.t(`monthlyInfo.heading.${budgetedEfficiencyHeading}`)}
                          </TableCell>
                          <TableCell align='center' className={cx(css.tableHeading, css.actionCell)}>
                            {hasPermission && i18n.t('monthlyInfo.heading.isActionRequired')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((item) => (
                          <SalesAndHoursRow
                            stationId={stationId}
                            date={date}
                            data={item}
                            key={item.id}
                            canEdit={hasPermission}
                            taxfree={taxfree}
                            refetch={refetch}
                          />
                        ))}
                        {!showOnly && (
                          <SalesAndHoursTotalRow
                            stationId={stationId}
                            date={date}
                            data={get(data, 'monthlyInfo', [])}
                            canEdit={hasPermission}
                            refetch={refetch}
                          />
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                )}
              </RestrictedConsumer>
            </div>
          </Loader>
        )
      }}
    </Query>
  )
}

type SalesAndHoursRowProps = {
  stationId: string
  date: string
  data: MonthlyInfoGeneral
  canEdit: boolean
  taxfree?: boolean
  refetch: (...args: Array<any>) => any
}

const SalesAndHoursRow = ({ stationId, date, data, canEdit, taxfree, refetch }: SalesAndHoursRowProps) => {
  const actualSales = Math.round(taxfree ? data.totalActualSalesTaxfree : data.totalActualSales) || 0
  const plannedSales = Math.round(data.totalPlannedSales) || 0
  const budgetedSales = Math.round(taxfree ? data.totalBudgetedSalesTaxfree : data.totalBudgetedSales) || 0
  const actualHours = Math.round(data.totalActualHours) || 0
  const plannedHours = Math.round(data.totalPlannedHours) || 0
  const actualEfficiency = calculateEfficiency(actualSales, actualHours)
  const budgetedEfficiency = Math.round(taxfree ? data.budgetedEfficiencyTaxfree : data.budgetedEfficiency) || 0
  const plannedEfficiency = Math.round(data.plannedEfficiency) || 0
  return (
    <TableRow>
      <TableCell align='center' className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`monthlyInfo.heading.profitCenter${data.profitCenterType}`)}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedSales} &euro;</strong>
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualSales >= plannedSales,
          [css.negativeCell]: actualSales < plannedSales,
        })}
      >
        {actualSales} &euro;
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedSales} &euro;
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualHours <= plannedHours,
          [css.negativeCell]: actualHours > plannedHours,
        })}
      >
        {actualHours} h
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedHours} h
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualEfficiency >= plannedEfficiency,
          [css.negativeCell]: actualEfficiency < plannedEfficiency,
        })}
      >
        {actualEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedEfficiency}</strong>
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            monthlyInfoId={data.id}
            subject='Management'
            isActionRequired={data.isActionRequired}
            refetch={refetch}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

type SalesAndHoursTotalRowProps = {
  stationId: string
  date: string
  data: Array<MonthlyInfoGeneral>
  canEdit: boolean
  refetch: (...args: Array<any>) => any
}

const SalesAndHoursTotalRow = ({ stationId, date, data, canEdit, refetch }: SalesAndHoursTotalRowProps) => {
  const {
    totalActualSales,
    totalPlannedSales,
    totalActualHours,
    totalPlannedHours,
    totalBudgetedSales,
    actualEfficiency,
    budgetedEfficiency,
    plannedEfficiency,
  } = calculateTotalData(data)
  return (
    <TableRow>
      <TableCell align='center' className={css.tableHeading}>
        {i18n.t('monthlyInfo.heading.total')}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{totalBudgetedSales} &euro;</strong>
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: totalActualSales >= totalPlannedSales,
          [css.negativeCell]: totalActualSales < totalPlannedSales,
        })}
      >
        {totalActualSales} &euro;
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {totalPlannedSales} &euro;
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: totalActualHours <= totalPlannedHours,
          [css.negativeCell]: totalActualHours > totalPlannedHours,
        })}
      >
        {totalActualHours} h
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {totalPlannedHours} h
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualEfficiency >= plannedEfficiency,
          [css.negativeCell]: actualEfficiency < plannedEfficiency,
        })}
      >
        {actualEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedEfficiency}</strong>
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            monthlyInfoId={data.id}
            subject='Management'
            isActionRequired={data.isActionRequired === undefined ? null : data.isActionRequired}
            refetch={refetch}
          />
        )}
      </TableCell>
    </TableRow>
  )
}
